import type { LessonPack, LessonPackListItem } from './schema.types';

/** Shown when nothing better is available. */
const UNTITLED = 'Untitled lesson';

/** Fallback estimate (minutes) used while a pack is still generating or when
 *  the backend hasn't computed a real duration yet. */
export const FALLBACK_ESTIMATE_MIN = 3;

type TitleSource = Pick<LessonPack, 'title' | 'chapterReference'> &
  Partial<Pick<LessonPack, 'inputMetadata'>>;

/**
 * Best display title for a pack. Prefers the real generated title, but while a
 * pack is still generating (or if the LLM omitted one) it falls back to the
 * requested topic → chapter → chapterReference so the UI never shows the bare
 * "Untitled lesson" default. Works for both full packs (have `inputMetadata`)
 * and list rows (only have `chapterReference`).
 */
export function packDisplayTitle(pack: TitleSource): string {
  const title = (pack.title ?? '').trim();
  if (title && title.toLowerCase() !== UNTITLED.toLowerCase()) return title;

  return (
    pack.inputMetadata?.topic?.trim() ||
    pack.inputMetadata?.chapter?.trim() ||
    pack.chapterReference?.trim() ||
    UNTITLED
  );
}

/**
 * Estimated total minutes for display, falling back to {@link FALLBACK_ESTIMATE_MIN}
 * when the backend value is missing or zero (e.g. mid-generation).
 */
export function packEstimatedMinutes(
  pack: Pick<LessonPack | LessonPackListItem, 'estimatedTotalMinutes'>,
): number {
  const m = pack.estimatedTotalMinutes;
  return typeof m === 'number' && m > 0 ? m : FALLBACK_ESTIMATE_MIN;
}
